/**
 * Offline queue for guest POST /api/guest/request-link — isolated from queuedContacts / pendingCardSave.
 * Storage: localStorage key velox_guest_save_queue
 */

import guestApi from './guestApi';
import { trackGuestImmediateSaveSuccess, trackGuestFollowupEmailSent } from './guestFunnelAnalytics';

export const GUEST_SAVE_QUEUE_STORAGE_KEY = 'velox_guest_save_queue';

/** Align with server utils/normalizeEmailVelox.js (guest merge + GuestSession email). */
export function normalizeEmailForGuestQueue(email) {
  if (!email || typeof email !== 'string') return '';
  const lowercasedEmail = email.toLowerCase().trim();
  const parts = lowercasedEmail.split('@');
  if (parts.length !== 2) return lowercasedEmail;

  const domain = parts[1];
  let localPart = parts[0];

  if (domain === 'gmail.com' || domain === 'googlemail.com') {
    localPart = localPart.replace(/\./g, '');
  }
  localPart = localPart.split('+')[0];

  return `${localPart}@${domain}`;
}

const TTL_MS = 7 * 24 * 60 * 60 * 1000;

const FLUSH_LOCK_NAME = 'velox_guest_save_flush';

let flushPromise = null;

function parseQueue(raw) {
  if (!raw || typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Drop entries older than 7 days and persist.
 * @returns {Array<{ email: string, normalizedEmail: string, nfcUID: string, queuedAt: number }>}
 */
export function pruneExpired() {
  if (typeof localStorage === 'undefined') return [];
  let list;
  try {
    list = parseQueue(localStorage.getItem(GUEST_SAVE_QUEUE_STORAGE_KEY));
  } catch {
    localStorage.removeItem(GUEST_SAVE_QUEUE_STORAGE_KEY);
    return [];
  }
  const cutoff = Date.now() - TTL_MS;
  const next = list.filter((item) => {
    const t = typeof item.queuedAt === 'number' ? item.queuedAt : 0;
    return t > cutoff;
  });
  if (next.length !== list.length) {
    try {
      localStorage.setItem(GUEST_SAVE_QUEUE_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore quota */
    }
  }
  return next;
}

export function readQueue() {
  return pruneExpired();
}

function writeQueue(list) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(GUEST_SAVE_QUEUE_STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* ignore quota */
  }
}

/**
 * Dedupe by (normalizedEmail + nfcUID), then append.
 * @param {{ email: string, nfcUID: string }} params
 */
export function enqueueGuestSave({ email, nfcUID }) {
  const trimmed = typeof email === 'string' ? email.trim() : '';
  const uid = typeof nfcUID === 'string' ? nfcUID.trim() : '';
  if (!trimmed || !uid) return;

  const normalizedEmail = normalizeEmailForGuestQueue(trimmed);
  let queue = readQueue();
  queue = queue.filter((item) => !(item.normalizedEmail === normalizedEmail && item.nfcUID === uid));
  queue.push({
    email: trimmed,
    normalizedEmail,
    nfcUID: uid,
    queuedAt: Date.now(),
  });
  writeQueue(queue);
}

function removeItem(item) {
  const queue = readQueue();
  const idx = queue.findIndex(
    (q) =>
      q.normalizedEmail === item.normalizedEmail &&
      q.nfcUID === item.nfcUID &&
      q.queuedAt === item.queuedAt
  );
  if (idx === -1) return;
  queue.splice(idx, 1);
  writeQueue(queue);
}

/** @param {import('axios').AxiosError} err */
function shouldKeepItemOnReplayError(err) {
  if (!err || !err.response) return true;
  const status = err.response.status;
  if (status === 429) return true;
  if (status >= 500) return true;
  if (status >= 400 && status < 500) return false;
  return true;
}

function fireAnalyticsAfterServerOk(res, nfcUID) {
  if (!res?.data?.ok) return;
  trackGuestImmediateSaveSuccess(nfcUID);
  if (res.data.emailSent) {
    trackGuestFollowupEmailSent(nfcUID);
  }
}

async function flushGuestSaveQueueInner() {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { synced: 0, remaining: readQueue().length };
  }

  let synced = 0;

  while (true) {
    const queue = readQueue();
    if (queue.length === 0) break;

    const item = queue[0];

    try {
      const res = await guestApi.post(
        '/api/guest/request-link',
        { email: item.email, nfcUID: item.nfcUID },
        { timeout: 60000 }
      );

      if (res.data?.ok) {
        removeItem(item);
        fireAnalyticsAfterServerOk(res, item.nfcUID);
        synced += 1;
      } else {
        removeItem(item);
      }
    } catch (err) {
      if (shouldKeepItemOnReplayError(err)) {
        break;
      }
      removeItem(item);
    }
  }

  return { synced, remaining: readQueue().length };
}

/**
 * Single-flight replay; uses Web Locks API when available to reduce duplicate work across tabs.
 * @returns {Promise<{ synced: number, remaining: number }>}
 */
export function flushGuestSaveQueue() {
  if (flushPromise) return flushPromise;

  const run = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function') {
        return await new Promise((resolve, reject) => {
          navigator.locks.request(FLUSH_LOCK_NAME, { mode: 'exclusive' }, async () => {
            try {
              const out = await flushGuestSaveQueueInner();
              resolve(out);
            } catch (e) {
              reject(e);
            }
          });
        });
      }
      return await flushGuestSaveQueueInner();
    } finally {
      flushPromise = null;
    }
  };

  flushPromise = run();
  return flushPromise;
}

export function getGuestSaveQueueLength() {
  return readQueue().length;
}

/** Max time to wait for guest cookie + server save before auth; merge needs cookie on same request. */
const PRE_AUTH_FLUSH_TIMEOUT_MS = 10000;

/**
 * If offline guest saves are pending, flush so HttpOnly cookie is set before the next auth request.
 * Never throws; on failure or timeout continues without blocking auth.
 * @returns {Promise<void>}
 */
export function tryFlushGuestSaveQueueBeforeAuth() {
  try {
    if (typeof window === 'undefined' || getGuestSaveQueueLength() === 0) {
      return Promise.resolve();
    }
  } catch {
    return Promise.resolve();
  }

  const pending = flushGuestSaveQueue();
  return Promise.race([
    pending,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('guest_flush_timeout')), PRE_AUTH_FLUSH_TIMEOUT_MS)
    ),
  ]).then(
    () => {},
    () => {}
  );
}

/**
 * True when the request failed in a way that should enqueue locally (offline / network / 5xx / 429).
 * @param {import('axios').AxiosError} err
 */
export function shouldEnqueueGuestSaveFromRequestError(err) {
  if (!err) return false;
  if (!err.response) return true;
  const status = err.response.status;
  if (status === 429) return true;
  if (status >= 500) return true;
  return false;
}
